import React, {useState} from 'react';
import {Pressable, ScrollView, Switch, Text, View} from 'react-native';
import i18n from 'i18n-js';
import {fontFamily} from '../../../helpers/fontFamily';
import RuleEngineDropdown from '../../rulesEngine/RuleEngineDropdown';
import {
  accentColor,
  groupBg,
  switchThumbColor,
  switchTrackColor,
  textColor,
  textInputBg,
  textMutedColor,
  toggleOffColor,
  touchButtonHeight,
  touchTargetMin
} from '../../../style/adminTools';
import {sheetRadius, sheetScrimColor} from '../../../style/adminTenantGrid';
import {
  DEFAULT_FILTERS,
  SORT_OPTIONS,
  STATUS_OPTIONS
} from './phoneSortOptions';

const t = (key, options) => i18n.t(`adminTenantGrid.${key}`, options);

const FieldLabel = ({children}) => (
  <Text
    style={{
      marginTop: 16,
      marginBottom: 6,
      fontFamily: fontFamily.font600,
      fontSize: 13,
      color: textMutedColor
    }}
  >
    {children}
  </Text>
);

const SheetButton = ({label, onPress, primary, testID}) => (
  <Pressable
    testID={testID}
    accessibilityRole="button"
    onPress={onPress}
    style={{
      height: touchButtonHeight,
      marginTop: 10,
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: 7,
      borderWidth: primary ? 0 : 2,
      borderColor: toggleOffColor,
      backgroundColor: primary ? accentColor : 'transparent'
    }}
  >
    <Text
      style={{fontFamily: fontFamily.font700, fontSize: 15, color: textColor}}
    >
      {label}
    </Text>
  </Pressable>
);

const PhoneFiltersSheet = ({initial, allCount, onApply, onClose}) => {
  const [draft, setDraft] = useState(initial);
  const update = changes => setDraft(current => ({...current, ...changes}));
  const sortOptions = SORT_OPTIONS.map(option => ({
    value: option.key,
    label: t(option.label)
  }));

  return (
    <View
      testID="phoneFiltersSheet"
      accessibilityRole="dialog"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'flex-end',
        zIndex: 5000
      }}
    >
      <Pressable
        testID="phoneFiltersScrim"
        onPress={onClose}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: sheetScrimColor
        }}
      />
      <View
        style={{
          maxHeight: '90%',
          backgroundColor: groupBg,
          borderTopLeftRadius: sheetRadius,
          borderTopRightRadius: sheetRadius
        }}
      >
        <ScrollView contentContainerStyle={{padding: 20, paddingBottom: 28}}>
          <Text
            accessibilityRole="header"
            style={{
              fontFamily: fontFamily.font700,
              fontSize: 18,
              color: textColor
            }}
          >
            {t('filtersTitle')}
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontFamily: fontFamily.font500,
              fontSize: 12,
              color: textMutedColor
            }}
          >
            {t('filtersSubtitle')}
          </Text>

          <FieldLabel>{t('filterStatus')}</FieldLabel>
          <View
            style={{
              flexDirection: 'row',
              backgroundColor: textInputBg,
              borderRadius: 8
            }}
          >
            {STATUS_OPTIONS.map(option => {
              const selected = draft.status === option.value;
              return (
                <Pressable
                  key={option.value}
                  testID={`phoneStatus-${option.value}`}
                  accessibilityRole="button"
                  accessibilityState={{selected}}
                  onPress={() => update({status: option.value})}
                  style={{
                    flex: 1,
                    minHeight: touchButtonHeight,
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 8,
                    backgroundColor: selected ? accentColor : 'transparent'
                  }}
                >
                  <Text
                    style={{
                      fontFamily: fontFamily.font600,
                      fontSize: 14,
                      color: textColor
                    }}
                  >
                    {t(option.label)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <FieldLabel>{t('sortBy')}</FieldLabel>
          <RuleEngineDropdown
            testID="phoneSortSelect"
            accessibilityLabel={t('sortBy')}
            options={sortOptions}
            selectedValue={draft.sortKey}
            onSelect={sortKey => update({sortKey})}
            triggerStyle={{minHeight: touchButtonHeight}}
          />

          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: touchTargetMin,
              marginTop: 16
            }}
          >
            <Text
              style={{
                fontFamily: fontFamily.font600,
                fontSize: 15,
                color: textColor
              }}
            >
              {t('allTenantSorting')}
            </Text>
            <Switch
              testID="phoneAllSortingSwitch"
              accessibilityLabel={t('allTenantSorting')}
              value={draft.allSorting}
              onValueChange={allSorting => update({allSorting})}
              trackColor={switchTrackColor}
              thumbColor={switchThumbColor}
            />
          </View>
          <Text
            testID="phoneAllSortingHelp"
            style={{
              marginBottom: 14,
              fontFamily: fontFamily.font500,
              fontSize: 12,
              color: textMutedColor
            }}
          >
            {t('allTenantSortingTooltip', {count: allCount})}
          </Text>

          <SheetButton
            primary
            testID="phoneFiltersApply"
            label={t('apply')}
            onPress={() => onApply(draft)}
          />
          <SheetButton
            testID="phoneFiltersReset"
            label={t('reset')}
            onPress={() => setDraft(DEFAULT_FILTERS)}
          />
        </ScrollView>
      </View>
    </View>
  );
};

export default PhoneFiltersSheet;
